<template>
  <v-card flat color="transparent" width="100%">
    <!-- Horizontal layout for breakpoints of sm and down -->
    <div class="hidden-md-and-up" v-if="products.length">
      <v-card-title class="px-0 py-2">
        <h2 class="text-break homepageTitle mb-1">
          {{ title }}
        </h2>
      </v-card-title>

      <v-card-text style="overflow-x: auto" class="px-0">
        <v-row
          v-if="products.length"
          class="flex-nowrap flex-md-wrap ma-0"
          dense
        >
          <template v-for="(product, index) in products">
            <v-col :key="index" cols="9" sm="5" md="12" class="px-0 pr-2">
              <v-hover v-slot="{ hover }">
                <v-card
                  color="grey lighten-5"
                  class="rounded"
                  :elevation="hover ? 5 : 1"
                  @click="productSelected(product)"
                >
                  <!-- image of the product -->
                  <v-img :aspect-ratio="1.618 / 1" :src="product.PictureUrl">
                    <!-- Highlighting label -->
                    <HighlightingLabel
                      :label="product.HighlightingLabel"
                      :visible="product.Highlighted"
                    />

                    <!-- sold out text -->
                    <div
                      v-if="!hasCapacity(product)"
                      class="red--text text-h6 font-weight-bold sold-out-lable"
                    >
                      {{ $translate('PRODUCT_SOLD_OUT') }}
                    </div>

                    <!-- button -->
                    <TrendingProductsButtons
                      :product="product"
                      :absoluteTopRight="true"
                      @add="openProductDialog(product)"
                      @info="productSelected(product)"
                    />
                  </v-img>

                  <div class="pa-2 text-subtitle-1 text-center text-truncate">
                    {{ product.Name }}
                  </div>

                  <div
                    class="px-2 pb-3 text-center itemTitleFontSize"
                    style="min-height: 80px"
                  >
                    <v-col class="pa-0">
                      <!-- Unit price -->
                      <v-row
                        v-if="showPrice(product)"
                        class="ma-0 text--primary d-inline-flex"
                        justify="center"
                        align="baseline"
                      >
                        <div
                          v-if="onDiscount(product)"
                          class="discount-price text-body-2 mr-1"
                        >
                          {{
                            formatter.format(getShownRetailUnitPrice(product))
                          }}
                        </div>
                        <v-row class="ma-0" justify="center" align="baseline">
                          <div>
                            <span
                              class="text-h6"
                              :class="
                                hasGeneralDiscount(product) ? 'red--text' : ''
                              "
                            >
                              {{ formatter.format(getShownUnitPrice(product)) }}
                            </span>
                            <span
                              v-if="isWeightedProduct(product)"
                              class="text-caption"
                            >
                              /
                              <span
                                v-if="product.WeightedInfo.OverviewSize > 1"
                              >
                                {{ product.WeightedInfo.OverviewSize }}
                              </span>
                              {{ product.WeightedInfo.Unit }}
                            </span>
                          </div>
                          <span class="text-caption text--secondary ml-1">
                            {{ priceSuffix }}
                          </span>
                        </v-row>
                      </v-row>

                      <!-- Minimum quantity -->
                      <div
                        v-if="
                          getDisplayMinQuantity(product) >
                          getDisplayStepSize(product)
                        "
                        class="text-caption text--primary"
                      >
                        Minimum
                        {{ getDisplayMinQuantity(product) }}
                        {{ getDisplayUnit(product) }}
                      </div>
                    </v-col>
                  </div>
                </v-card>
              </v-hover>
            </v-col>
          </template>
        </v-row>
        <div v-else class="py-3 text-center text--secondary">
          {{ $translate('TRENDING_NO_ITEMS') }}
        </div>
      </v-card-text>
    </div>

    <!-- Vertical layout for breakpoints of md and up -->
    <div class="hidden-sm-and-down">
      <v-row justify="center" align="center" class="mx-0" no-gutters>
        <v-col cols="11">
          <v-card color="primary" elevation="1" tile>
            <v-card-text class="px-3 py-2">
              <h3
                class="primary-font-color homepageSubTitle text-h6 font-weight-bold text-break"
              >
                {{ title }}
              </h3>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card-text
        class="px-4 pt-0 mt-3"
        :style="`max-height: 1200px; overflow-y: auto;`"
      >
        <v-list v-if="products.length" three-line color="transparent">
          <template v-for="(product, index) in products">
            <v-list-item
              :key="`product-${index}`"
              class="px-0 pb-1"
              @click="productSelected(product)"
            >
              <!-- Picture, visible for lg and up breakpoints -->
              <v-list-item-avatar
                size="80"
                rounded
                width="40%"
                class="hidden-md-and-down"
              >
                <v-img :src="product.PictureUrl" />

                <!-- Highlighting label -->
                <HighlightingLabel
                  :label="product.HighlightingLabel"
                  :visible="product.Highlighted"
                />

                <!-- sold out text -->
                <div
                  v-if="!hasCapacity(product)"
                  class="red--text text-caption font-weight-bold sold-out-lable"
                >
                  {{ $translate('PRODUCT_SOLD_OUT') }}
                </div>
              </v-list-item-avatar>

              <v-list-item-content>
                <!-- Picture, visible for md and down breakpoints -->
                <v-avatar size="80" rounded class="hidden-lg-and-up mb-2">
                  <v-img :src="product.PictureUrl" />
                </v-avatar>

                <!-- Name -->
                <v-list-item-subtitle
                  class="text-center"
                  v-html="product.Name"
                />

                <!-- Price -->
                <v-list-item-title
                  v-if="showPrice(product)"
                  class="my-1 text-center"
                >
                  <v-row no-gutters justify="center">
                    <v-col
                      v-if="onDiscount(product)"
                      class="discount-price text-body-2"
                    >
                      {{ formatter.format(getShownRetailUnitPrice(product)) }}
                    </v-col>
                    <v-col
                      :class="
                        hasGeneralDiscount(product)
                          ? 'red--text'
                          : 'text--primary'
                      "
                    >
                      {{ formatter.format(getShownUnitPrice(product)) }}
                    </v-col>
                  </v-row>
                </v-list-item-title>

                <!-- Button to add product, visible for lg and down breakpoints -->
                <v-row
                  justify="center"
                  style="width: 100%"
                  class="hidden-xl-only mt-1"
                >
                  <TrendingProductsButtons
                    :product="product"
                    @add="openProductDialog(product)"
                    @info="productSelected(product)"
                  />
                </v-row>
              </v-list-item-content>

              <!-- Button to add product, visible for xl breakpoint -->
              <v-list-item-action class="hidden-lg-and-down">
                <TrendingProductsButtons
                  :product="product"
                  @add="openProductDialog(product)"
                  @info="productSelected(product)"
                />
              </v-list-item-action>
            </v-list-item>

            <v-divider
              v-if="index < products.length - 1"
              :key="`divider-${index}`"
            />
          </template>
        </v-list>
        <div v-else class="py-3 text-center text--secondary">
          {{ $translate('TRENDING_NO_ITEMS') }}
        </div>
      </v-card-text>
    </div>

    <!-- product dialog -->
    <ProductOrderDialog
      :showDialog="showProductDialog"
      :product="selectedProduct"
      @close="showProductDialog = false"
    />
  </v-card>
</template>

<script>
import trendingProductsMixin from '@/mixins/trendingProducts';

export default {
  name: 'TrendingProductsClassic',
  mixins: [trendingProductsMixin],
};
</script>

<style scoped lang="scss">
.sold-out-lable {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
}
</style>
